import { supabase } from "@/integrations/supabase/client";

const PENDING_KEY = "lysara_pending_portal_auth_diagnostics";
const CONTEXT_KEY = "lysara_portal_auth_diagnostic_context";
const ACTION = "portal_auth_diagnostic";
const RESOURCE_TYPE = "portal_auth_diagnostic";
const MAX_QUEUED_EVENTS_PER_FLUSH = 2;
let diagnosticFlushPromise: Promise<void> | null = null;
let pendingFlushContext: DiagnosticContext | null = null;

type Portal = "outlet" | "factory" | "other";
type DiagnosticDetails = Record<string, string | number | boolean | null | undefined>;

interface DiagnosticContext {
  userId: string;
  tenantId: string | null;
  portal: Portal;
}

interface PendingEvent {
  subjectUserId: string;
  eventId: string;
  event: string;
  occurredAt: string;
  queuedAt?: string;
  tenantId: string | null;
  portal: Portal;
  details: DiagnosticDetails;
}

function portalFromPath(): Portal {
  if (typeof window === "undefined") return "other";
  const path = window.location.pathname;
  if (path.startsWith("/outlet-")) return "outlet";
  if (path.startsWith("/factory-")) return "factory";
  return "other";
}

function browserFamily(): string {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/CriOS|Chrome/i.test(ua)) return "Chrome";
  if (/FxiOS|Firefox/i.test(ua)) return "Firefox";
  if (/EdgiOS|Edg\//i.test(ua)) return "Edge";
  if (/Safari/i.test(ua)) return "Safari";
  return "other";
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function safeDetails(details: DiagnosticDetails): DiagnosticDetails {
  const allowed = new Set([
    "reason", "source", "scope", "auth_event", "code", "status", "portal", "lease_state",
    "service", "resource", "method", "error_code", "refresh_error_code", "channel_status",
    "realtime_event_seen",
    "navigation_type", "tab_session_id", "pin_challenge_present", "validation_attempt",
  ]);
  return Object.fromEntries(
    Object.entries(details).filter(([key, value]) => allowed.has(key) &&
      (value === null || ["string", "number", "boolean"].includes(typeof value))),
  );
}

function deviceSessionId(): string {
  // Keep this browser identifier across tab closes/reopens so delayed events
  // can be correlated. The per-tab ID below distinguishes concurrent tabs.
  const key = "lysara_portal_auth_diagnostic_device";
  try {
    let id = window.localStorage.getItem(key);
    if (!id) {
      id = crypto.randomUUID();
      window.localStorage.setItem(key, id);
    }
    return id;
  } catch {
    return "unavailable";
  }
}

function tabSessionId(): string {
  const key = "lysara_portal_auth_diagnostic_tab";
  try {
    let id = window.sessionStorage.getItem(key);
    if (!id) {
      id = crypto.randomUUID();
      window.sessionStorage.setItem(key, id);
    }
    return id;
  } catch { return "unavailable"; }
}

function navigationType(): string {
  try {
    const entry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    return entry?.type ?? "unknown";
  } catch { return "unknown"; }
}

function diagnosticEnvelope(event: PendingEvent, queuedReplay = false) {
  const occurredAtMs = Date.parse(event.occurredAt);
  return {
    category: "portal_auth",
    event: event.event,
    eventId: event.eventId,
    tenantId: event.tenantId,
    portal: event.portal,
    occurredAt: event.occurredAt,
    delivery: queuedReplay ? "queued_replay" : "immediate",
    queuedAt: event.queuedAt ?? null,
    queueDelayMs: queuedReplay && Number.isFinite(occurredAtMs)
      ? Math.max(0, Date.now() - occurredAtMs)
      : 0,
    path: window.location.pathname,
    online: navigator.onLine,
    visibility: document.visibilityState,
    browser: browserFamily(),
    deviceSessionId: deviceSessionId(),
    tabSessionId: tabSessionId(),
    navigationType: navigationType(),
    buildId: (import.meta.env as ImportMetaEnv & { VITE_LOVABLE_LOAD_ID?: string }).VITE_LOVABLE_LOAD_ID ?? import.meta.env.MODE,
    details: event.details,
  };
}

function enqueue(event: PendingEvent): void {
  const pending = readJson<PendingEvent[]>(PENDING_KEY, []);
  const queuedEvent = { ...event, queuedAt: event.queuedAt ?? new Date().toISOString() };
  const byId = new Map<string, PendingEvent>();
  for (const item of pending) {
    if (item && typeof item.eventId === "string") byId.set(item.eventId, item);
  }
  if (!byId.has(queuedEvent.eventId)) byId.set(queuedEvent.eventId, queuedEvent);
  try {
    window.localStorage.setItem(PENDING_KEY, JSON.stringify([...byId.values()].slice(-30)));
  } catch {
    // Local diagnostics must never affect authentication behavior.
  }
}

type DiagnosticWriteOutcome = "written" | "retry" | "rejected";

async function writeEvent(
  context: DiagnosticContext,
  event: PendingEvent,
  queuedReplay = false,
): Promise<DiagnosticWriteOutcome> {
  const request = supabase.functions.invoke("record-client-diagnostic", {
    body: { ...diagnosticEnvelope(event, queuedReplay), tenantId: event.tenantId ?? context.tenantId },
  });
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = await Promise.race([
      request,
      new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), 1500); }),
    ]);
    if (!result) return "retry";
    if (!result.error && result.data?.ok === true) return "written";

    const error = result.error as { status?: number; context?: { status?: number } } | null;
    const status = error?.status ?? error?.context?.status;
    if (status === 403 || result.data?.error === "Tenant access denied") return "rejected";
    return "retry";
  } catch {
    return "retry";
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function flushPendingDiagnosticsForContext(context: DiagnosticContext): Promise<void> {
  const pending = readJson<PendingEvent[]>(PENDING_KEY, []);
  const byId = new Map<string, PendingEvent>();
  for (const item of pending) {
    if (item && typeof item.eventId === "string") byId.set(item.eventId, item);
  }

  const all = [...byId.values()];
  const matchingUser = all.filter((item) => item.subjectUserId === context.userId);
  // A device can retain events from a previous tenant. The server correctly
  // rejects those reports for the current user, so discard them here.
  const matching = matchingUser.filter((item) => item.tenantId === context.tenantId);
  const toFlush = matching.slice(0, MAX_QUEUED_EVENTS_PER_FLUSH);
  if (toFlush.length === 0) {
    const remaining = all.filter((item) =>
      item.subjectUserId !== context.userId || item.tenantId === context.tenantId,
    );
    try { window.localStorage.setItem(PENDING_KEY, JSON.stringify(remaining.slice(-30))); } catch { /* ignore */ }
    return;
  }

  const retryIds = new Set<string>();
  for (const event of toFlush) {
    const outcome = await writeEvent(context, event, true).catch(() => "retry" as const);
    if (outcome === "retry") retryIds.add(event.eventId);
  }

  const attemptedIds = new Set(toFlush.map((event) => event.eventId));
  const latest = readJson<PendingEvent[]>(PENDING_KEY, []);
  const remainingById = new Map<string, PendingEvent>();
  for (const item of latest) {
    if (!item || typeof item.eventId !== "string") continue;
    if (item.subjectUserId === context.userId && item.tenantId !== context.tenantId) continue;
    if (attemptedIds.has(item.eventId) && !retryIds.has(item.eventId)) continue;
    remainingById.set(item.eventId, item);
  }
  for (const event of toFlush) {
    if (retryIds.has(event.eventId) && !remainingById.has(event.eventId)) {
      remainingById.set(event.eventId, event);
    }
  }
  try {
    window.localStorage.setItem(PENDING_KEY, JSON.stringify([...remainingById.values()].slice(-30)));
  } catch {
    // Diagnostics must never affect authentication behavior.
  }
}

function flushPendingDiagnostics(context: DiagnosticContext): Promise<void> {
  pendingFlushContext = context;
  if (!diagnosticFlushPromise) {
    diagnosticFlushPromise = (async () => {
      while (pendingFlushContext) {
        const nextContext = pendingFlushContext;
        pendingFlushContext = null;
        await flushPendingDiagnosticsForContext(nextContext);
      }
    })().finally(() => {
      diagnosticFlushPromise = null;
      // A caller can queue a context just as the loop is finishing. Start one
      // more pass after releasing the single-flight slot so it cannot be lost.
      if (pendingFlushContext) void flushPendingDiagnostics(pendingFlushContext);
    });
  }
  return diagnosticFlushPromise;
}

export async function logPortalAuthDiagnostic(
  event: string,
  details: DiagnosticDetails = {},
  contextOverride: { tenantId?: string | null; portal?: Portal } = {},
): Promise<void> {
  try {
    if (typeof window === "undefined") return;
    const occurredAt = new Date().toISOString();
    const portal = contextOverride.portal ?? portalFromPath();
    const tenantId = contextOverride.tenantId ?? window.localStorage.getItem("selectedTenantId");
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.user?.id) {
      const saved = readJson<DiagnosticContext | null>(CONTEXT_KEY, null);
      if (saved?.userId) enqueue({
        subjectUserId: saved.userId,
        eventId: crypto.randomUUID(),
        event,
        occurredAt,
        tenantId: tenantId ?? saved.tenantId,
        portal,
        details: safeDetails(details),
      });
      return;
    }

    const context: DiagnosticContext = { userId: session.user.id, tenantId, portal };
    try {
      window.localStorage.setItem(CONTEXT_KEY, JSON.stringify(context));
    } catch {
      // Ignore unavailable storage.
    }

    const current: PendingEvent = {
      subjectUserId: context.userId,
      eventId: crypto.randomUUID(),
      event,
      occurredAt,
      tenantId,
      portal,
      details: safeDetails(details),
    };
    const outcome = await writeEvent(context, current).catch(() => "retry" as const);
    if (outcome === "retry") enqueue(current);

    // A sign-out can clear the JWT before its diagnostic event reaches Postgres.
    // Serialize queue flushes so overlapping auth callbacks cannot replay the
    // same queued event concurrently. The original occurredAt remains intact.
    await flushPendingDiagnostics(context);
  } catch {
    // Diagnostics must never change sign-in, sign-out, or authorization behavior.
  }
}
